import { useForm, FormProvider } from "react-hook-form";
import PropTypes from "prop-types";
import { useEffect } from "react";

/**
 * A wrapper for react-hook-form that handles form state and submission.
 * It provides a form context to its children, so that form fields
 * can be used without passing down props.
 * @param {object} defaultValues - The default values for the form.
 * @param {React.ReactNode} children - The form fields.
 * @param {function} onSubmit - An async function to call when the form is submitted. It should return true on success.
 * @param {function} resolver - The validation resolver (e.g., from yupResolver).
 * @param {object} context - The context for validation schemas.
 * @param {object} otherProps - Other props to pass to the form element.
 * @returns {JSX.Element}
 */
const Form = ({
  defaultValues,
  children,
  onSubmit,
  onChange,
  resolver,
  context,
  watchFields = [],
  mode,
  ...otherProps
}) => {
  const methods = useForm({ defaultValues, resolver, context, mode });
  const { handleSubmit, reset } = methods;
  const { watch } = methods;

  useEffect(() => {
    if (!onChange) return;

    const subscription = watch((values, info) => {
      if (!watchFields || watchFields.includes(info.name)) {
        onChange(values, info, methods);
      }
    });

    return () => subscription.unsubscribe();
  }, [watch, onChange, watchFields, methods]);

  const handleFormSubmit = async (data) => {
    try {
      // The onSubmit prop is expected to be an async function.
      // We pass the form methods so the consumer can call setError if needed.
      const success = await onSubmit(data, methods);

      // If the submission was successful, reset the form with the new values
      // to clear the dirty state.
      if (success) {
        reset(data);
      }
    } catch (error) {
      // The onSubmit function should handle its own errors, but we catch
      // any unhandled ones here to prevent the app from crashing.
      console.error("Unexpected error occurred during form submission:", error);
    }
  };

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={handleSubmit(handleFormSubmit)}
        noValidate
        {...otherProps}
      >
        {children}
      </form>
    </FormProvider>
  );
};

Form.propTypes = {
  defaultValues: PropTypes.object,
  children: PropTypes.node.isRequired,
  onSubmit: PropTypes.func.isRequired,
  resolver: PropTypes.func,
  context: PropTypes.object,
  mode: PropTypes.oneOf(["onBlur", "onChange", "onSubmit", "onTouched", "all"]),
};

export default Form;
